import { useState, useCallback, useEffect } from 'react';
import { DateRange } from '@/types/DateRange.interface';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { getBranchTimezone, presetDateRange } from '@/utils/businessDay';

interface UseDateFilterOptions {
  defaultPreset?: DateRange['preset'];
  onDateRangeChange?: (dateRange: DateRange | null) => void;
}

interface UseDateFilterResult {
  dateRange: DateRange;
  setDateRange: (dateRange: DateRange | null) => void;
  getDateRangeParams: () => {
    startDate: string;
    endDate: string;
    timezone: string;
  };
  dateRangeQueryParams: string;
}

/**
 * Custom hook for handling date filtering
 * @param options Configuration options
 * @returns Date filter state and utilities
 */
export const useDateFilter = (
  options?: UseDateFilterOptions
): UseDateFilterResult => {
  const { defaultPreset = 'last.30days', onDateRangeChange } = options || {};
  const { t, i18n } = useTranslation();

  // Initialize date range — İŞLETME günü (07:00 → ertesi 03:00) üzerinden;
  // aralıklar web POS raporuyla aynı (`presetDateRange`).
  const [dateRange, setDateRangeState] = useState<DateRange>(() => {
    const preset = defaultPreset === 'custom' ? 'last.30days' : defaultPreset;
    return {
      ...presetDateRange(preset),
      preset,
      label: t(`filters.date.${preset}`)
    };
  });

  // Set date range with callback
  const setDateRange = useCallback(
    (newDateRange: DateRange | null) => {
      if (newDateRange) {
        setDateRangeState(newDateRange);
        if (onDateRangeChange) {
          onDateRangeChange(newDateRange);
        }
      } else if (onDateRangeChange) {
        onDateRangeChange(null);
      }
    },
    [onDateRangeChange]
  );

  // Update labels when language changes
  useEffect(() => {
    if (dateRange.preset) {
      setDateRangeState((prevRange) => ({
        ...prevRange,
        label: t(`filters.date.${prevRange.preset}`)
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language, t]);

  const getDateRangeParams = useCallback(() => {
    const timezone = getBranchTimezone();

    return {
      startDate: format(dateRange.startDate, 'yyyy-MM-dd'),
      endDate: format(dateRange.endDate, 'yyyy-MM-dd'),
      timezone
    };
  }, [dateRange]);

  // Get URL query parameters for date range
  const dateRangeQueryParams = useCallback(() => {
    const { startDate, endDate, timezone } = getDateRangeParams();
    return `startDate=${startDate}&endDate=${endDate}&timezone=${encodeURIComponent(timezone)}`;
  }, [getDateRangeParams]);

  return {
    dateRange,
    setDateRange,
    getDateRangeParams,
    dateRangeQueryParams: dateRangeQueryParams()
  };
};

export default useDateFilter;
